import React, { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useMotionValue, useTransform, animate, type PanInfo } from "framer-motion";
import {
  Lightbulb,
  Info,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  ArrowLeft,
  X,
  Volume2,
  VolumeX,
  Megaphone,
} from "lucide-react";
import { Deck } from "@/types/linguadeck";
import { getOrGenerateTestData } from "@/lib/testData";
import { useGamification } from "@/contexts/GamificationContext";
import { useAuth } from "@/contexts/AuthContext";
import { recordCardInteraction } from "@/lib/db";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { getCardSkinStyles } from "@/lib/cardSkins";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";

const LABELS = ["A", "B", "C", "D"];

interface TestStudyViewProps {
  deck: Deck;
  onNext?: () => void;
  onMark?: (correct: boolean) => void;
  onTestComplete?: (result: { correctCount: number; total: number }) => void;
  onEdit?: () => void;
  onMenuClick?: () => void;
  onFullscreen?: () => void;
  onBack?: () => void;
  /** Mobile fullscreen mode: 100dvh layout, card wrapper, minimal header (matches FlashcardView) */
  fullscreenMobile?: boolean;
}

export default function TestStudyView({
  deck,
  onNext,
  onMark,
  onTestComplete,
  onBack,
  fullscreenMobile = false,
}: TestStudyViewProps) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const { user } = useAuth();

  const { finishStudySession, equippedCardSkin } = useGamification();
  const cardSkinStyles = getCardSkinStyles(equippedCardSkin);
  const { isVoiceEnabled, voiceVolume, toggleVoice } = useSoundStore();

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [hintExpanded, setHintExpanded] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);
  const [sessionAnswers, setSessionAnswers] = useState<
    Record<number, boolean>
  >({});

  const cardsWithTestData = useMemo(() => {
    return deck.cards
      .filter((c) => (c.front || "").trim())
      .map((card) => ({
        card,
        testData: getOrGenerateTestData(card, deck.cards, lang),
      }));
  }, [deck.cards, lang]);

  const currentCard = cardsWithTestData[cardIndex]?.card;
  const total = cardsWithTestData.length;
  const localized = cardsWithTestData[cardIndex]?.testData ?? null;

  const isLocked = selectedIndex !== null;
  const isCorrect =
    isLocked && localized && selectedIndex === localized.correctIndex;

  useEffect(() => {
    setHintExpanded(false);
  }, [cardIndex]);

  const handleSelect = (idx: number) => {
    if (isLocked) return;
    const correct = localized ? idx === localized.correctIndex : false;
    setSelectedIndex(idx);
    setSessionAnswers((prev) => ({ ...prev, [cardIndex]: correct }));
    if (localized && onMark) {
      onMark(correct);
    }
    if (user?.id && currentCard?.id && deck.id) {
      recordCardInteraction({
        userId: user.id,
        cardId: currentCard.id,
        deckId: deck.id,
        mode: "test",
        answeredCorrectly: correct,
        hintUsed: hintExpanded,
      }).catch((err) => console.warn("[Adaptive] recordCardInteraction failed:", err));
    }
  };

  const handleNext = () => {
    const isLastCard = cardIndex === total - 1;
    const willComplete = isLastCard && selectedIndex !== null;

    if (willComplete) {
      const allAnswers = { ...sessionAnswers, [cardIndex]: !!isCorrect };
      const correctCount = Object.values(allAnswers).filter(Boolean).length;
      if (total > 0) {
        finishStudySession(total, true);
      }
      if (total >= 10) {
        useNotificationStore.getState().addNotification({
          kind: "TEST_COMPLETE",
          displayType: "ROUTE",
          target: "/library",
          iconType: "study",
        });
      }
      onTestComplete?.({ correctCount, total });
      setSessionAnswers({});
    }

    setSelectedIndex(null);
    setHintExpanded(false);
    setCardIndex((i) => (i + 1) % Math.max(1, total));
    onNext?.();
  };

  const speakQuestion = () => {
    const text = (localized?.question || "").trim();
    if (text && isVoiceEnabled && currentCard) {
      void playAudio({
        card: { ...currentCard, front: text, back: "" },
        side: "front",
        deckLang: deck.lang ?? "en",
        volume: voiceVolume ?? 1,
      });
    }
  };

    if (total === 0) {
    if (fullscreenMobile && onBack) {
      return (
        <div className="flex flex-col h-[100dvh] w-full bg-background overflow-hidden overscroll-x-none pt-[env(safe-area-inset-top)]" style={{ overscrollBehaviorX: "none" }}>
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 h-12">
            <button
              type="button"
              onClick={onBack}
              className="flex items-center justify-center w-10 h-10 rounded-full text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--bg3))] active:opacity-70 transition-colors"
              aria-label={t("studyMode.close")}
            >
              <X size={24} strokeWidth={2} />
            </button>
            <div className="flex-1" />
          </div>
          <div className="flex-1 flex items-center justify-center px-4">
            <div className="w-[95%] max-w-[550px] mx-auto bg-white dark:bg-[hsl(var(--card-bg))] rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-10 text-center">
              <ClipboardList className="w-14 h-14 mx-auto mb-4 text-slate-300 dark:text-[hsl(var(--muted-foreground))]" />
              <p className="text-lg font-bold text-slate-800 dark:text-[hsl(var(--foreground))] mb-1">
                {t("testMode.noTestCards")}
              </p>
              <p className="text-sm text-slate-400 dark:text-[hsl(var(--muted-foreground))]">
                {t("testMode.noTestCardsHint")}
              </p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <div className="w-[95%] max-w-[550px] mx-auto bg-white dark:bg-[hsl(var(--card-bg))] rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-10 text-center">
          <ClipboardList className="w-14 h-14 mx-auto mb-4 text-slate-300 dark:text-[hsl(var(--muted-foreground))]" />
          <p className="text-lg font-bold text-slate-800 dark:text-[hsl(var(--foreground))] mb-1">
            {t("testMode.noTestCards")}
          </p>
          <p className="text-sm text-slate-400 dark:text-[hsl(var(--muted-foreground))]">
            {t("testMode.noTestCardsHint")}
          </p>
        </div>
      </div>
    );
  }

  if (!currentCard || !localized) return null;

  const filledCount = cardIndex + (isLocked ? 1 : 0);
  const progressPct = Math.min(100, (filledCount / total) * 100);

  // Swipe gestures — lower thresholds for easier swipes: short deliberate drag (80px) OR fast flick (400 velocity)
  const SWIPE_OFFSET_THRESHOLD = 80;
  const SWIPE_VELOCITY_THRESHOLD = 400;
  const EXIT_OFFSET = 450;
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-250, 0, 250], [-12, 0, 12]);
  const opacityLeft = useTransform(x, [-250, -SWIPE_OFFSET_THRESHOLD, 0], [0.95, 0.5, 0]);
  const opacityRight = useTransform(x, [0, SWIPE_OFFSET_THRESHOLD, 250], [0, 0.5, 0.95]);
  const scaleLeft = useTransform(x, [-200, -60, 0], [1.15, 0.85, 0.7]);
  const scaleRight = useTransform(x, [0, 60, 200], [0.7, 0.85, 1.15]);
  // Dynamic background tint: green on swipe right, orange on swipe left (max opacity ~0.25)
  const opacityGreen = useTransform(x, [0, 80, 200], [0, 0.5, 1]);
  const opacityOrange = useTransform(x, [-200, -80, 0], [1, 0.5, 0]);
  const isExitingRef = useRef(false);

  const resetCardPosition = () => x.set(0);
  useEffect(() => {
    resetCardPosition();
  }, [cardIndex]);

  const handleDragEnd = async (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (isExitingRef.current) return;
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    const isRightSwipe = offset > SWIPE_OFFSET_THRESHOLD || velocity > SWIPE_VELOCITY_THRESHOLD;
    const isLeftSwipe = offset < -SWIPE_OFFSET_THRESHOLD || velocity < -SWIPE_VELOCITY_THRESHOLD;
    const spring = { type: "spring" as const, stiffness: 300, damping: 25 };
    if (isRightSwipe || isLeftSwipe) {
      isExitingRef.current = true;
      await animate(x, isRightSwipe ? EXIT_OFFSET : -EXIT_OFFSET, spring);
      handleNext();
      isExitingRef.current = false;
      resetCardPosition();
    } else {
      animate(x, 0, spring);
    }
  };

  const nextCardData = cardIndex + 1 < total ? cardsWithTestData[cardIndex + 1] : null;

  // Fullscreen mobile layout — matches FlashcardView shell (100dvh, card wrapper, minimal header)
  if (fullscreenMobile) {
    return (
      <div className="flex flex-col h-[100dvh] w-full bg-background overflow-hidden overscroll-x-none pt-[env(safe-area-inset-top)]" style={{ overscrollBehaviorX: "none" }}>
        {/* Thin progress bar at top */}
        <div className="flex-shrink-0 h-[3px] w-full bg-[hsl(var(--bg3))]">
          <motion.div
            className="h-full rounded-r-full"
            style={{
              width: `${progressPct}%`,
              background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))",
            }}
            transition={{ duration: 0.3 }}
          />
        </div>
        {/* Minimal header: Close (X), Progress, Mute toggle */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 h-12">
          <div className="w-10 flex justify-start">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-10 h-10 rounded-full text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--bg3))] active:opacity-70 transition-colors"
                aria-label={t("studyMode.close")}
              >
                <X size={24} strokeWidth={2} />
              </button>
            )}
          </div>
          <span className="text-sm font-medium text-[hsl(var(--foreground))] tabular-nums">
            {t("studyMode.progress", { current: cardIndex + 1, total })}
          </span>
          <div className="w-10 flex justify-end">
            <button
              type="button"
              onClick={toggleVoice}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                isVoiceEnabled
                  ? "text-[hsl(var(--primary))] hover:bg-[hsl(var(--bg3))]"
                  : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--bg3))] opacity-70"
              } active:opacity-70`}
              aria-label={isVoiceEnabled ? t("flashcard.audio_on") : t("flashcard.audio_off")}
              title={isVoiceEnabled ? t("flashcard.audio_on") : t("flashcard.audio_off")}
            >
              {isVoiceEnabled ? (
                <Volume2 size={22} strokeWidth={2} />
              ) : (
                <VolumeX size={22} strokeWidth={2} />
              )}
            </button>
          </div>
        </div>

        {/* Card area: swipe labels + card stack + draggable card — overscroll-x-none blocks iOS swipe-back */}
        <div className="flex-1 min-h-0 flex flex-col relative px-4 py-2 overscroll-x-none" style={{ perspective: "1200px", overscrollBehaviorX: "none" }}>
          {/* Swipe labels */}
          <motion.div
            className="absolute left-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 z-[60] pointer-events-none origin-center"
            style={{ opacity: opacityLeft, scale: scaleLeft }}
          >
            <span className="text-xl font-bold text-amber-500 dark:text-amber-400 whitespace-nowrap drop-shadow-sm">
              {t("studyMode.stillLearning")}
            </span>
          </motion.div>
          <motion.div
            className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 z-[60] pointer-events-none origin-center"
            style={{ opacity: opacityRight, scale: scaleRight }}
          >
            <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap drop-shadow-sm">
              {t("studyMode.know")}
            </span>
          </motion.div>

          {/* Stack: next card behind */}
          {nextCardData && (
            <div className="absolute inset-0 flex items-center justify-center px-4 py-2" style={{ zIndex: 0 }}>
              <div
                className="w-[92%] max-w-md h-full rounded-2xl border border-[hsl(var(--border))] overflow-hidden"
                style={{
                  ...cardSkinStyles,
                  background: "hsl(var(--card-bg))",
                  transform: "scale(0.94) translateY(8px)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                }}
              >
                <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                  <p className="text-xl text-[hsl(var(--muted-foreground))]/50 line-clamp-3">
                    {nextCardData.testData?.question ?? ""}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Draggable card — 1:1 finger tracking, touch-action: pan-y */}
          <motion.div
            className="absolute inset-0 flex flex-col px-4 py-2 cursor-grab active:cursor-grabbing z-10"
            style={{ x, rotate, touchAction: "pan-y" }}
            drag="x"
            dragConstraints={{ left: -500, right: 500 }}
            dragElastic={0.7}
            dragDirectionLock
            onDragEnd={handleDragEnd}
          >
            <div
              className="w-[92%] max-w-md flex-1 min-h-[280px] self-center rounded-2xl border border-[hsl(var(--border))] overflow-hidden flex flex-col relative"
              style={{
                ...cardSkinStyles,
                background: "hsl(var(--card-bg))",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              {/* Dynamic background tint overlay — green (swipe right) / orange (swipe left) */}
              <div className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden z-[1]">
                <motion.div
                  className="absolute inset-0 rounded-2xl"
                  style={{ backgroundColor: "rgba(34, 197, 94, 0.25)", opacity: opacityGreen }}
                />
                <motion.div
                  className="absolute inset-0 rounded-2xl"
                  style={{ backgroundColor: "rgba(249, 115, 22, 0.25)", opacity: opacityOrange }}
                />
              </div>
              {/* Image: flex-1 expands to fill space; w-full object-cover, no letterboxing */}
              {currentCard.imageUrl ? (
                <div className="flex-1 min-h-[200px] w-full overflow-hidden rounded-t-2xl">
                  <img
                    src={currentCard.imageUrl}
                    alt=""
                    className="h-full w-full object-cover rounded-t-xl"
                  />
                </div>
              ) : (
                <div className="flex-1 min-h-[200px]" aria-hidden />
              )}
              {/* Bottom block: Word + hint + feedback + Answer tiles — grouped together */}
              <div className="flex flex-col justify-end flex-shrink-0 px-4 pt-4 pb-4">
                <div className="flex items-center justify-center gap-2 w-full mb-2">
                  <h2 className="text-2xl md:text-3xl font-black text-[hsl(var(--foreground))] text-center leading-tight flex-1">
                    {localized.question}
                  </h2>
                  {isVoiceEnabled && (
                    <button
                      type="button"
                      onClick={speakQuestion}
                      className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-[hsl(var(--bg3))]/60 text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))] transition-colors"
                      aria-label={t("flashcard.listen")}
                    >
                      <Megaphone size={18} strokeWidth={2} />
                    </button>
                  )}
                </div>

                {localized.hint && (
                  <button
                    type="button"
                    onClick={() => setHintExpanded((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-indigo-400 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors mb-1"
                  >
                    <Lightbulb className="w-3.5 h-3.5" />
                    {t("testMode.hint")}
                  </button>
                )}

                {hintExpanded && localized.hint && (
                  <div className="w-full mb-2 px-4 py-2 rounded-xl bg-indigo-50/80 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-800/40">
                    <p className="text-sm text-[hsl(var(--foreground))] leading-relaxed text-center">
                      {localized.hint}
                    </p>
                  </div>
                )}

                {isLocked && (
                  <div
                    className={`w-full mb-2 flex items-center justify-center gap-2 rounded-xl px-4 py-2 ${
                      isCorrect
                        ? "bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-700/50"
                        : "bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-700/50"
                    }`}
                  >
                    {isCorrect ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <Info className="h-4 w-4 shrink-0 text-red-500 dark:text-red-400" />
                    )}
                    <p
                      className={`text-sm font-semibold ${
                        isCorrect
                          ? "text-emerald-700 dark:text-emerald-300"
                          : "text-red-600 dark:text-red-300"
                      }`}
                    >
                      {isCorrect
                        ? t("testMode.correctFeedback")
                        : t("testMode.incorrectFeedback")}
                    </p>
                  </div>
                )}

                {/* Answer tiles (A, B, C, D) — stacked vertically, close to word */}
                <div className="flex flex-col gap-2 pt-1">
              {LABELS.slice(0, Math.min(4, localized.options.length)).map(
                (label, idx) => {
                  const text = localized.options[idx] ?? "";
                  const chosen = selectedIndex === idx;
                  const correct = localized.correctIndex === idx;
                  const showCorrect = isLocked && correct;
                  const showWrong = isLocked && chosen && !correct;

                  let tileBorder = "border-[hsl(var(--border))]";
                  let tileBg = "bg-[hsl(var(--card-bg))]";
                  let hoverClass =
                    "hover:border-indigo-400 hover:bg-indigo-50/50 dark:hover:border-indigo-500 dark:hover:bg-indigo-950/20 cursor-pointer active:scale-[0.98]";
                  let badgeBg =
                    "bg-[hsl(var(--bg3))] text-[hsl(var(--muted-foreground))]";

                  if (showCorrect) {
                    tileBorder = "border-emerald-400 dark:border-emerald-500";
                    tileBg = "bg-emerald-50 dark:bg-emerald-950/40";
                    hoverClass = "cursor-default";
                    badgeBg = "bg-emerald-500 text-white";
                  } else if (showWrong) {
                    tileBorder = "border-red-400 dark:border-red-500";
                    tileBg = "bg-red-50 dark:bg-red-950/40";
                    hoverClass = "cursor-default";
                    badgeBg = "bg-red-500 text-white";
                  } else if (isLocked) {
                    hoverClass = "cursor-default opacity-60";
                  }

                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={isLocked}
                      onClick={() => handleSelect(idx)}
                      className={`border-2 ${tileBorder} ${tileBg} rounded-2xl py-3 px-4 flex items-center gap-4 transition-all duration-200 w-full text-left ${hoverClass}`}
                    >
                      <span
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${badgeBg}`}
                      >
                        {label}
                      </span>
                      <span className="text-base font-medium text-[hsl(var(--foreground))]">
                        {text}
                      </span>
                    </button>
                  );
                }
              )}
            </div>

                {/* Footer: Next button */}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!isLocked}
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))] text-white font-bold text-lg shadow-lg hover:opacity-95 transition-transform active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center justify-center gap-2"
                  >
                    {t("testMode.next")}
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
        </motion.div>
      </div>
    </div>);
  }

  // Legacy scrollable layout (desktop / non-fullscreen)
  return (
    <div className="flex flex-col items-center justify-start flex-1 min-h-0 pt-4 pb-8 px-2">
      <div className="w-[95%] max-w-[550px] mt-2 mb-auto mx-auto bg-white dark:bg-[hsl(var(--card-bg))] rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col">
        {/* ── Header: back button (optional) + progress bar ── */}
        <div className="px-6 pt-4 pb-3">
          <div className="flex items-center justify-between mb-2 gap-2">
            <span className="flex items-center gap-2 min-w-0">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="shrink-0 p-1.5 -ml-1 rounded-lg hover:bg-slate-100 dark:hover:bg-[hsl(var(--bg3))] text-slate-600 dark:text-[hsl(var(--muted-foreground))] transition-colors"
                  aria-label={t("action.back")}
                >
                  <ArrowLeft size={20} strokeWidth={2} />
                </button>
              )}
              <span className="text-xs font-semibold text-slate-400 dark:text-[hsl(var(--muted-foreground))] tracking-wide uppercase truncate">
                {deck.title}
              </span>
            </span>
            <span className="text-xs font-bold text-slate-500 dark:text-[hsl(var(--muted-foreground))] tabular-nums shrink-0">
              {t("studyMode.progress", { current: cardIndex + 1, total })}
            </span>
          </div>
          <div className="h-3 w-full rounded-full overflow-hidden bg-slate-100 dark:bg-[hsl(var(--border))]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* ── Body: image + word + hint link ── */}
        <div className="flex flex-col items-center px-6 pb-1">
          {currentCard.imageUrl && (
            <div className="w-full max-w-[200px] max-h-[140px] rounded-2xl bg-slate-50 dark:bg-[hsl(var(--secondary))] flex items-center justify-center overflow-hidden mb-2">
              <img
                src={currentCard.imageUrl}
                alt=""
                className="max-h-[140px] w-auto object-contain"
              />
            </div>
          )}
          <h2 className="text-3xl font-black text-slate-800 dark:text-[hsl(var(--foreground))] text-center mb-1 leading-tight">
            {localized.question}
          </h2>

          {localized.hint && (
            <button
              type="button"
              onClick={() => setHintExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-medium text-indigo-400 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
            >
              <Lightbulb className="w-3.5 h-3.5" />
              {t("testMode.hint")}
            </button>
          )}

          {hintExpanded && localized.hint && (
            <div className="w-full mt-1 mb-1 px-4 py-2 rounded-xl bg-indigo-50/80 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-800/40">
              <p className="text-sm text-slate-600 dark:text-[hsl(var(--foreground))] leading-relaxed text-center">
                {localized.hint}
              </p>
            </div>
          )}

          {isLocked && (
            <div
              className={`w-full mt-1.5 flex items-center justify-center gap-2 rounded-xl px-4 py-2 ${
                isCorrect
                  ? "bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-700/50"
                  : "bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-700/50"
              }`}
            >
              {isCorrect ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <Info className="h-4 w-4 shrink-0 text-red-500 dark:text-red-400" />
              )}
              <p
                className={`text-sm font-semibold ${
                  isCorrect
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-red-600 dark:text-red-300"
                }`}
              >
                {isCorrect
                  ? t("testMode.correctFeedback")
                  : t("testMode.incorrectFeedback")}
              </p>
            </div>
          )}
        </div>

        {/* ── Answer tiles (A, B, C, D) ── */}
        <div className="flex flex-col gap-y-2 px-6 pt-3 pb-3">
          {LABELS.slice(0, Math.min(4, localized.options.length)).map(
            (label, idx) => {
              const text = localized.options[idx] ?? "";
              const chosen = selectedIndex === idx;
              const correct = localized.correctIndex === idx;
              const showCorrect = isLocked && correct;
              const showWrong = isLocked && chosen && !correct;

              let tileBorder = "border-slate-100 dark:border-[hsl(var(--border))]";
              let tileBg = "bg-white dark:bg-[hsl(var(--card-bg))]";
              let hoverClass =
                "hover:border-indigo-400 hover:bg-indigo-50 dark:hover:border-indigo-500 dark:hover:bg-indigo-950/20 cursor-pointer active:scale-[0.98]";
              let badgeBg =
                "bg-slate-100 dark:bg-[hsl(var(--border))] text-slate-500 dark:text-[hsl(var(--muted-foreground))]";

              if (showCorrect) {
                tileBorder = "border-emerald-400 dark:border-emerald-500";
                tileBg = "bg-emerald-50 dark:bg-emerald-950/40";
                hoverClass = "cursor-default";
                badgeBg = "bg-emerald-500 text-white";
              } else if (showWrong) {
                tileBorder = "border-red-400 dark:border-red-500";
                tileBg = "bg-red-50 dark:bg-red-950/40";
                hoverClass = "cursor-default";
                badgeBg = "bg-red-500 text-white";
              } else if (isLocked) {
                hoverClass = "cursor-default opacity-60";
              }

              return (
                <button
                  key={idx}
                  type="button"
                  disabled={isLocked}
                  onClick={() => handleSelect(idx)}
                  className={`border-2 ${tileBorder} ${tileBg} rounded-2xl py-3 px-4 flex items-center gap-4 transition-all duration-200 w-full text-left ${hoverClass}`}
                >
                  <span
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${badgeBg}`}
                  >
                    {label}
                  </span>
                  <span className="text-base font-medium text-slate-700 dark:text-[hsl(var(--foreground))]">
                    {text}
                  </span>
                </button>
              );
            }
          )}
        </div>

        {/* ── Footer: full-width Next button ── */}
        <div className="px-6 pb-5 pt-1">
          <button
            type="button"
            onClick={handleNext}
            disabled={!isLocked}
            className="w-full py-4 rounded-2xl bg-indigo-600 text-white font-bold text-lg shadow-lg hover:bg-indigo-700 transition-transform active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center justify-center gap-2"
          >
            {t("testMode.next")}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
